
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { convertToParamMap, ParamMap, Params, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
	catchError,
	concatMap,
	exhaustMap,
	map,
	switchMap,
	tap
} from 'rxjs/operators';
import { ErrorMessage, ErrorType } from '../../domain/error/error-message.model';
import { decodeError, manageError } from '../../domain/error/error.util';
import { loadUser, loadUserGuest, loadUserSoft, resetUser } from '../../domain/user/user.actions';
import { openDialog } from '../../shared/dialog/dn-dialog.actions';
import { PermissionDialogComponent } from '../../shared/dialog/permission-dialog/permission-dialog.component';
import { PermissionVisualizationType } from '../../shared/dialog/permission-dialog/permission.model';
import {
	getOneAndWaitforLoaded,
	getOneAndWaitforOnline,
	onlyWhenOnline
} from '../../shared/util/operators';
import { saveClickLog } from '../clicks/click.actions';
import { ClickLog, ClickOperation } from '../clicks/click.models';
import { ClickService } from '../clicks/click.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LogService } from '../log/log.service';
import { navigateHome, navigateOffline } from '../router/routes.actions';
import { createHardLoginSession, createSession, resetSession } from '../session/session.actions';
import { selectSessionID } from '../session/session.selectors';
import {
	authCheckOnline,
	authGuestLogin,
	authGuestLoginFailure,
	authGuestLoginSuccess,
	authHardLogin,
	authHardLoginFailure,
	authHardLoginSuccess,
	authLoadingUser,
	authLogout,
	authLogoutAndGuest,
	authLogoutAndHard,
	authNotLogged,
	authOffline,
	authOnline,
	authRenewToken,
	authRenewTokenFailure,
	authRenewTokenSuccess,
	authSetLoginType,
	authSetRenewToken,
	authSoftLogin,
	authSoftLoginFailure,
	authSoftLoginSuccess,
	noOpAction,
	setLocalStorageAuth
} from './auth.actions';
import { AuthLoginState, HealthCheck, LoginType, ServerAuthData, Token } from './auth.models';
import {
	selectAuthLoginState,
	selectGetAccessToken,
	selectIsAuthenticated
} from './auth.selectors';
import { AuthService } from './auth.service';

//  import {ServerAuthData} from './auth.models'
export const AUTH_KEY = 'AUTH';

@Injectable({ providedIn: 'root' })
export class AuthEffects {
	//  login hard
	login$ = createEffect(() => {
		const loginData: {
			email: string;
			password: string;
			rememberMe: boolean;
			loginType: LoginType;
		} = {
			email: '',
			password: '',
			rememberMe: false,
			loginType: LoginType.HARDLOGIN
		};
    let params: ParamMap;
    let oldSessionID: number;
    let url: string;
    let referrer: string

		return this.actions$.pipe(
			ofType(authHardLogin),
			onlyWhenOnline(this.store),
			getOneAndWaitforLoaded(this.store),
			tap((action) => this.logService.info('Effect: authLogin', action)),
			switchMap((action) => {
				loginData.email = action.payloadSignin.email;
				loginData.password = action.payloadSignin.password;
				loginData.rememberMe = action.payloadSignin.rememberMe;
				loginData.loginType = action.payloadSignin.loginType;
        params = action.params;
        oldSessionID = action.oldSessionID;
        url = action.url;
        referrer = action.referrer;

				return this.authService
					.login(action.payloadSignin.email, action.payloadSignin.password, action.payloadSignin.rememberMe, action.payloadSignin.loginType)
					.pipe(
						map((serverAuthData) => authHardLoginSuccess({ serverAuthData,params,oldSessionID,url,referrer })),
						catchError((error: any) => {
							// create click log on login failure
							const clickLog: ClickLog = <ClickLog>{};
							clickLog.clickLogOperationID = ClickOperation.Login;
							clickLog.content = JSON.stringify(loginData);
							clickLog.message = error.messageDN;
							clickLog.method = 'POST';
							clickLog.outcome = false;
							this.store.dispatch(saveClickLog({ clickLog }));

							return of(authHardLoginFailure({ error }));
						})
					);
			})
		);
	});

	loginSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(authHardLoginSuccess),
				tap((payload) => {
					this.logService.info('Effect: authHardLoginSuccess. ', payload.serverAuthData);
					this.storageService.setItem('LOGINTYPE', LoginType.HARDLOGIN);
				}),
				concatMap((payload) => [
          // crea nuova sessione
          createHardLoginSession({params: payload.params, oldSessionID: payload.oldSessionID,url: payload.url, referrer: payload.referrer }),
					loadUser({ idAnagrafica: payload.serverAuthData.IDAnagrafica }),
					authLoadingUser()
				])
			)
		//  { dispatch: false },
	);

	//  guest login
	guestLogin$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authGuestLogin),
			onlyWhenOnline(this.store),
			getOneAndWaitforLoaded(this.store),
			tap((action) => this.logService.info('Effect: authGuestLogin', action)),
			switchMap(() =>
				this.authService.login('', '', true, LoginType.GUESTLOGIN).pipe(
					tap((serverAuthData: ServerAuthData) =>
						this.logService.info('After guest login. Received:', serverAuthData)
					),
					map((serverAuthData: ServerAuthData) => authGuestLoginSuccess({ serverAuthData })),
					catchError((error: any) => of(authGuestLoginFailure({ error })))
				)
			)
		)
	);

	guestLoginFailure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authGuestLoginFailure),
			concatLatestFrom(() => this.store.select(selectAuthLoginState)),
			tap(([payload, authLoginState]) => {
				this.logService.info('Effect: authGuestLoginFailure', payload.error);
			}),
			map(([{ error }, authLoginState]) => manageError(error, authLoginState))
		)
	);

	guestLoginSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authGuestLoginSuccess),
			tap((payload) => {
				this.logService.info('Effect: authGuestLoginSuccess. ', payload.serverAuthData);
				this.storageService.setItem('LOGINTYPE', LoginType.GUESTLOGIN);
			}),
			map((payload) => loadUserGuest({})),
			catchError((error: any) => of(authGuestLoginFailure({ error })))
		)
	);

	// renew
	renew$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authRenewToken),
			onlyWhenOnline(this.store),
			getOneAndWaitforLoaded(this.store),
			tap((action) => this.logService.info('Effect: authRenewToken', action)),
			exhaustMap(() => {
				// new-login (2.10): la credenziale renew è il cookie HttpOnly; localStorage
				// non porta più nulla. In migrazione un DOTTNET-RENEW legacy viene inoltrato
				// UNA volta nel body; altrimenti renew alla cieca (solo cookie). CU-30/32.
				const legacy = this.storageService.getItem('RENEW') as Token | null;
				const renew$ = legacy
					? this.authService.renewToken(legacy)
					: this.authService.renewToken();
				return renew$.pipe(
					map((serverAuthData) =>
						authRenewTokenSuccess({ serverAuthData: serverAuthData })
					),
					catchError((error: any) => of(authRenewTokenFailure({ error })))
				);
			}) //  exhaustmap
		)
	);

	renewSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authRenewTokenSuccess),
			tap((payload) => {
				this.logService.info('Effect: authRenewSuccess ', payload.serverAuthData);
				// new-login (2.10): il renew token vive nel cookie HttpOnly. La credenziale
				// legacy in localStorage viene CANCELLATA dopo il primo renew riuscito e mai
				// più ripersistita. CU-32.
				this.storageService.removeItem('RENEW');
			}),
			map((payload) => {
				switch (payload.serverAuthData.loginType) {
					case LoginType.GUESTLOGIN:
						return loadUserGuest({});
					case LoginType.HARDLOGIN:
						return loadUser({ idAnagrafica: payload.serverAuthData.IDAnagrafica });
					case LoginType.SOFTLOGIN:
						return loadUserSoft({ idAnagrafica: payload.serverAuthData.IDAnagrafica });
					default:
						const errorMessage: ErrorMessage = {
							Code: ErrorType.ErrorTipoLoginScorretto,
							message: 'Incorrect Login type',
							messageDN: 'Incorrect Login type',
							name: 'Incorrect Login type'
						};
						return authRenewTokenFailure({ error: errorMessage });
				}
			}),
			catchError((error: any) => of(authRenewTokenFailure({ error })))
		)
	);

	setRenewToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authSetRenewToken),
			tap((action) => this.logService.info('Effect: authSetRenewToken', action)),
			map((action) => authRenewToken()),
			catchError((error: any) => of(authRenewTokenFailure({ error })))
		)
	);

	//  login soft
	softLogin$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authSoftLogin),
			onlyWhenOnline(this.store),
			getOneAndWaitforLoaded(this.store),
			tap((action) => this.logService.info('Effect: authLoginSoft', action)),
			switchMap((action) =>
				this.authService.softLogin(action.token).pipe(
					map((serverAuthData) => authSoftLoginSuccess({ serverAuthData })),
					catchError((error: any) => of(authSoftLoginFailure({ error })))
				)
			)
		)
	);

	//  check if online
	authCkeckOnline$ = createEffect(() => {
		let savedAuthLoginState: AuthLoginState = AuthLoginState.LOGGEDGUEST;
		return this.actions$.pipe(
			ofType(authCheckOnline),
			concatLatestFrom(() => this.store.select(selectAuthLoginState)),
			tap(([_, authLoginState]) => {
				savedAuthLoginState = authLoginState;

				this.logService.info('Effect: authCheckOnline');
			}),
			switchMap((action) =>
				this.authService.checkOnline().pipe(
					map((healthCheck: HealthCheck) => {
						this.logService.info('authCheckOnline calling authOnline:', healthCheck);
						return authOnline();
					}),
					catchError((error: any) => {
						this.logService.info('authCheckOnline calling manageError:', error);
						return of(manageError(error, savedAuthLoginState));
					})
				)
			)
		);
	});

	loginSoftSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(authSoftLoginSuccess),
				tap((payload) => {
					this.logService.info('Effect: authSoftLoginSuccess. ', payload.serverAuthData);
					this.storageService.setItem('LOGINTYPE', LoginType.SOFTLOGIN);
				}),
				concatMap((payload) => [
					authLoadingUser(),
					loadUserSoft({ idAnagrafica: payload.serverAuthData.IDAnagrafica })
				]),
				catchError((error: any) => of(authSoftLoginFailure({ error })))
			)
		//  { dispatch: false },
	);

	online$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authOnline),
			// first time backend goes offline let authOffline execute.url
			// then filter all subsequent requests until backend is ONLINE again
			onlyWhenOnline(this.store),
			tap(() => {
				this.logService.info('Effect: authOnline. Server back online', undefined);
			}),
			// new-login (1.5): concatLatestFrom, non combineLatestWith. Con
			// combineLatestWith l'effetto ri-emette a OGNI cambio di
			// selectIsAuthenticated dopo il primo authOnline: entrare in REFRESHING
			// rende isAuthenticated falso → authGuestLogin, un demote provocato dal
			// rinnovo stesso. concatLatestFrom legge il valore solo all'arrivo di authOnline.
			concatLatestFrom(() => this.store.select(selectIsAuthenticated)),
			map(([_, isAuthenticated]) => {
				if (!isAuthenticated) {
					// new-login (2.10): cold boot = renew alla cieca PRIMA del guest login.
					// Il cookie renew (se presente) rilogga l'utente di ritorno; un 401 →
					// loginerror → authGuestLogin (fallback). CU-30/31.
					return authRenewToken();
				} else {
					return noOpAction();
				}
			})
		)
	);

	offline$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authOffline),
			// first time backend goes offline let authOffline execute.url
			// then filter all subsequent requests until backend is ONLINE again
			getOneAndWaitforOnline(this.store),
			tap(() => {
				this.logService.info('Effect: authOffline. Server went offline', undefined);
			}),
			// Needed to create a copy of the router.url.
			// If omitted, we would pass the reference to this.router.url which would change
			// POC: remove 448, change currentUrl to { currentUrl: this.router.url } and tapping the value in navigateOffline
			map(() => this.router.url),
			map((currentUrl) => navigateOffline({ currentUrl }))
		)
	);

	// logout

	logout = createEffect(() =>
		this.actions$.pipe(
			ofType(authLogout),
			// let go only first call, then waits until internal observable emits. This way if there are many calls to the same effect, only one is executed
			onlyWhenOnline(this.store),
			getOneAndWaitforLoaded(this.store),
			concatLatestFrom(() => this.store.select(selectGetAccessToken)),
			tap((action) => {
				this.logService.info('Effect: authLogout', action);
				this.storageService.removeItem('RENEW');
				this.storageService.removeItem('LOGINTYPE');
				this.storageService.removeItem('USER');
				this.storageService.deleteStorageStartingWith('DRCLICK')
				this.storageService.deleteStorageStartingWith('oauth2')

			}),
			switchMap(([_, accessToken]) => {
				// Record "Logout OK" with the still-valid hard token BEFORE /logout
				// invalidates its family — otherwise the log 401s, call-service renews,
				// and the retry records it under the post-logout guest (wrong identity).
				const clickLog: ClickLog = <ClickLog>{};
				clickLog.clickLogOperationID = ClickOperation.Logout;
				clickLog.content = '';
				clickLog.message = 'Logout OK';
				clickLog.method = 'GET';
				clickLog.outcome = true;

				return this.clickService.CreateClickLog(clickLog).pipe(
					// best-effort: a failed log must not block the logout
					catchError(() => of(null)),
					// concatMap (not switchMap): the log round-trip completes before /logout is sent
					concatMap(() => this.authService.logout(accessToken)),
					tap((serverAuthData: ServerAuthData) =>
						this.logService.info('After logout. Received:', serverAuthData)
					),
					concatLatestFrom(() => this.store.select(selectSessionID)),
					concatMap(([serverAuthData, oldSessionID]) => [
						navigateHome(),
						resetUser(),
						resetSession(),
						authGuestLoginSuccess({ serverAuthData }),
						createSession({
							params: convertToParamMap(<Params>{}),
							oldSessionID: oldSessionID,
							url: this.router.url,
							referrer: this.ssrDocument.referrer
						})
					]),
					catchError((error: any) => of(authGuestLoginFailure({ error })))
				);
			})
		)
	);

	loginerror = createEffect(() =>
		this.actions$.pipe(
			ofType(authRenewTokenFailure, authHardLoginFailure, authSoftLoginFailure),
			concatLatestFrom(() => this.store.select(selectAuthLoginState)),
			tap((action) => this.logService.info('Effect: ', action)),
			map(([action, authLoginState]) => {
				//  if I am already Guest, no need to guest login again
				if (authLoginState === AuthLoginState.LOGGEDGUEST) {
					return loadUserGuest({});
				}

				// TODO: here we need to evaluate action.type and authLoginState
				// if authLoginState is NOLOGGED, we need to pass something else as state
				// so that manageError knows what action trigger
				// So if action.type is renewTokenFailure we need to check LOGINTYPE localstorage element
				// if action.type is authHardLoginFailure we need to pass HARDLOGGED
				// if action.type is authSoftLoginFailure we need to pass SOFTLOGGED

				if (authLoginState === AuthLoginState.NOTLOGGED) {
					if (action.type === authRenewTokenFailure.type) {
						const currentLoginType = <LoginType>this.storageService.getItem('LOGINTYPE');
						switch (currentLoginType) {
							case LoginType.GUESTLOGIN:
								authLoginState = AuthLoginState.LOGGEDGUEST;
								break;
							case LoginType.HARDLOGIN:
								authLoginState = AuthLoginState.LOGGEDHARD;
								break;
							case LoginType.SOFTLOGIN:
								authLoginState = AuthLoginState.LOGGEDSOFT;
								break;
						}
					}
					if (action.type === authHardLoginFailure.type) {
						authLoginState = AuthLoginState.LOGGEDHARD;
					}
					if (action.type === authSoftLoginFailure.type) {
						authLoginState = AuthLoginState.LOGGEDSOFT;
					}
				}

				// Only wipe persisted auth on terminal auth errors. A transient
				// network/backend error must not destroy a valid session.
				const decoded = decodeError(action.error, null);
				const isTerminalAuthError =
					decoded.Code === ErrorType.ErrorRenewTokenScaduto ||
					decoded.Code === ErrorType.ErrorRenewTokenInvalidato ||
					decoded.Code === ErrorType.ErrorUtenteNonAutorizzato ||
					decoded.Code === ErrorType.ErrorCookieSessioneNonPresente ||
					decoded.Code === ErrorType.ErrorSoftTokenScaduto;

				if (isTerminalAuthError) {
					this.storageService.removeItem('RENEW');
					this.storageService.removeItem('USER');
					this.storageService.removeItem('LOGINTYPE');
					this.storageService.deleteStorageStartingWith('DRCLICK');
					this.storageService.deleteStorageStartingWith('oauth2');
				}

				this.logService.info('Effect: authLogin/Renew failure. dispatching guest login', undefined);

				return manageError(action.error, authLoginState);
			})
			/* ,
			catchError((error: any) => of(manageError(error)))
		*/
		)
	);

	setLocalStorageAuth$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setLocalStorageAuth),
			tap(() => this.logService.info('Effect: setLocalStorageAuth')),
			concatMap(({ token, loginType }) => [
				authSetRenewToken({ token }),
				authSetLoginType({ loginType })
			])
		)
	);

	authLogoutandGuest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authLogoutAndGuest),
			tap(() => this.logService.info('Effect: authLogoutAndGuest')),
			concatMap(() => [authNotLogged(), resetUser(), resetSession(), authGuestLogin()])
		)
	);

	authLogoutandHard$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authLogoutAndHard),
			tap(() => this.logService.info('Effect: authLogoutAndHard')),
			concatMap(() => [
				authNotLogged(),
				resetUser(),
				resetSession(),
				authGuestLogin(),
				openDialog({
					content: undefined,
					urlToDownload:undefined,
					urlWithContext:undefined,
					componentType: PermissionDialogComponent,
					componentName: 'PermissionDialogComponent',
					disableClose: true,
					data: {
						permissionType: PermissionVisualizationType.UserSessionExpired
					}
				})
			])
		)
	);

	/*	authNotLogged$$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authNotLogged),
			tap(() => this.logService.info('Effect: authNotLogged')),
			map(() =>	resetUser())
		)
	);
*/
	constructor(
		private actions$: Actions,
		private authService: AuthService,
		private router: Router,
		private storageService: LocalStorageService,
		private store: Store,
		private logService: LogService,
		private clickService: ClickService,
		@Inject(DOCUMENT) private ssrDocument: Document
	) {}
}

